import React, { useState, useEffect, useRef, useCallback } from 'react';
import TimeAgo from 'react-timeago';
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell
} from "@heroui/table";
import {
  Spinner,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Dropdown,
  DropdownItem,
  DropdownTrigger,
  DropdownSection,
  DropdownMenu
} from "@heroui/react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "@heroui/modal";
import CheckoutFormEnvelope from './CheckoutFormEnvelope';
import clipboardImage from '../images/clipboard.png';
import EmailRecordsModal from './EmailRecordsModal';
import CompletionModal from './CompletionModal';
import ResumeModal from './ResumeModal';
import UserInfo from './UserInfo';
import { useInfiniteScroll } from "@heroui/use-infinite-scroll";

const CampaignTable = ({user, editCampaign, stompClient}) => {
  const campaignData = useRef([]);
  const [fetching, setFetching] = useState(false);
  const [fetchingCounters, setFetchingCounters] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const [upgradeCampaign, setUpgradeCampaign] = useState(false);
  const [recordsCampaign, setRecordsCampaign] = useState();
  const [upgradeAmount, setUpgradeAmount] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState();
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showUser, setShowUser] = useState();
  const [recordCount, setRecordCount] = useState();
  const [clickCount, setClickCount] = useState();
  const [pages, setPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const perPage = 10;
  const page = useRef(1);
  const sortDescriptor = useRef({direction: 'descending', column: 'created_at'});

  useEffect(() => {
    setHasMore(true);
    setCampaignData([]);
    page.current = 1;
  }, [user]);

  const setCampaignData = (data) => {
    campaignData.current = data;
  };

  const deleteCampaign = async (cid) => {
    const result = await fetch(`/api/v1/campaign/${cid}`, {method: 'DELETE'});
    if (result.status === 200) {
      alert(`Campaign ${cid} deleted successfully.`);
      window.location.reload();
    }
  };

  const fetchCounters = useCallback(async () => {
    if (!fetchingCounters) {
      setFetchingCounters(true);
      const result = await fetch('/api/v1/counters');
      const data = await result.json();
      if (data['counters']) {
        setClickCount(data['counters']['clicks']);
        setRecordCount(data['counters']['emails']);
      }
      setFetchingCounters(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const fetchCounterData = async () => {
      await fetchCounters();
    }
    fetchCounterData();
    if (user?.active_organization_id) {
      stompClient?.subscribe(`/topic/orgs/${user?.active_organization_id}`, (message) => handleStompMessage(message));
    } else if (user?.admin) {
      stompClient?.subscribe('/topic/admin', (message) => handleStompMessage(message));
    } else {
      stompClient?.subscribe(`/topic/users/${user?._id || user?.id}_campaigns`, (message) => handleStompMessage(message));
    }
    // eslint-disable-next-line
  }, [user, stompClient]);

  const fetchCampaignData = useCallback(async () => {
    if (!fetching) {
      if (pages > 1 && page.current > pages) {
        // At the end.
        setHasMore(false);
        setFetching(false);
        return;
      }
      setFetching(true);
      const sd = sortDescriptor.current;
      const cdUrl = `/api/v1/users/${user.id}/campaigns?page=${page.current}&per_page=${perPage}&sort=${sd.column}&direction=${sd.direction}`;
      const result = await fetch(cdUrl);
      const data = await result.json();
      if (data && result.status === 200) {
        // Success... Hide form and show status.
        setPages(data['total_pages']);
        if (page.current === 1) {
          setCampaignData(data['campaigns']);
          if (data['campaigns'].length === 0 || data['campaigns'] === [] || data['total_pages'] === 1) {
            setHasMore(false);
          }
        } else {
          const newCampaignData = [...campaignData.current];
          setCampaignData(newCampaignData.concat(data['campaigns']));
        }
        page.current = page.current + 1;
        if (page.current > data['total_pages']) {
          setHasMore(false);
        }
      }
      setFetching(false);
    }
  }, [user, pages, fetching]);

  const [loaderRef, scrollerRef] = useInfiniteScroll({hasMore, onLoadMore: fetchCampaignData});

  const handleStompMessage = async (m) => {
    // Message format:
    //  {
    //    "email_record": {
    //      "_id":"6811627d380ae39ddca33cad",
    //      "campaign_id":"680a4cc1d400d03c12008e0f",
    //      "created_at":"2025-04-29T23:36:29.426Z",
    //      "fullname":null,
    //      "source":"wedge@foofyay.com",
    //      "updated_at":"2025-04-29T23:36:29.426Z"
    //    }
    //  }
    const message = JSON.parse(m.body);
    const mraw = JSON.stringify(m);
    // console.log('[Stomp message]: ' + JSON.stringify(message));
    if (message['email_record'] && (!user.active_organization_id || mraw.indexOf('/topic/orgs/') !== -1)) {
      setRecordCount((counter) => counter + 1);
      let newDataTmp = [...campaignData.current];
      const newData = await Promise.all(
        newDataTmp.map(c => {
          if (c['_id'] === message['email_record']['campaign_id']) {
            c['email_record_count'] += 1;
          }
          return c;
        })
      );
      setCampaignData(newData);
    } else if (message['usage'] && (!user.active_organization_id || mraw.indexOf('/topic/orgs/') !== -1)) {
      setClickCount((counter) => counter + parseInt(message['usage']['count']));
      let newDataTmp = [...campaignData.current];
      const newData = await Promise.all(
        newDataTmp.map(c => {
          if (c['_id'] === message['usage']['campaign_id']) {
            c['usage_counter'] += message['usage']['count'];
          }
          return c;
        })
      );
      setCampaignData(newData);
    } else if (message['campaign_update']) {
      // This is when the actual campaign is updated, not the usage_counter
      let newDataTmp = [...campaignData.current];
      const newData = await Promise.all(
        newDataTmp.map(c => {
          if (c['_id'] === message['campaign_update']['campaign_id']) {
            c['updated_at'] = message['campaign_update']['updated_at'];
          }
          return c;
        })
      );
      setCampaignData(newData);
    } else if (message['populated'] && (!user.active_organization_id || mraw.indexOf('/topic/orgs/') !== -1)) {
      let newDataTmp = [...campaignData.current];
      const newData = await Promise.all(
        newDataTmp.map(c => {
          if (c['_id'] === message['populated']['campaign_id']) {
            c['populated'] = message['populated']['status'];
          }
          return c;
        })
      );
      setCampaignData(newData);
    }
  };

  const genUrl = (campaign) => {
    if (user?.admin) {
      let aurl = `${window.location.protocol}//${window.location.host}/${campaign.vanity_url}`;
      return <a href={`${aurl}`}>{campaign.vanity_url}</a>;
    }
    let finalUrl = `${window.location.protocol}//${window.location.host}/${campaign.vanity_url}`;
    if (finalUrl.indexOf('activistmailer') !== -1) {
      finalUrl = finalUrl.replace('activistmailer', 'ActivistMailer');
    }
    return <a href={finalUrl}>{finalUrl}</a>;
  };

  const upgrade = (e, campaign, amount) => {
    const pop = document.getElementById(`pop_${campaign._id}`);
    pop.style.display = 'none';
    e.preventDefault();
    setUpgradeAmount(amount);
    setShowUpgradeModal(true);
    setUpgradeCampaign(campaign)
  };

  const generatePopover = (campaign) => {
    return <Popover id={`pop_${campaign._id}`}>
      <PopoverTrigger>
        <Button size="sm" color='success' isIconOnly>
          <span style={{fontWeight: 'bold', fontSize: '18px', color: '#202020'}}>{String.fromCharCode('65291')}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent>
         <Button style={{margin: '10px'}} onClick={(e) => upgrade(e, campaign, 500)}>+100</Button>
         <Button style={{margin: '10px'}} onClick={(e) => upgrade(e, campaign, 4000)}>+1000</Button>
         <Button style={{margin: '10px'}} onClick={(e) => upgrade(e, campaign, 9900)}>Unlimited</Button>
      </PopoverContent>
    </Popover>
  };

  const setCopied = (campaign) => {
    campaignData.current.forEach(d => {
      const c = document.getElementById(`copied_${d._id}`);
      if (d._id === campaign._id) {
        c.innerText = 'copied!';
      } else {
        c.innerText = '';
      }
    });
  };

  const ellipse = (string, limit=30) => {
    let newString = string;
    if (string.length > limit) {
      newString = string.substring(0, limit);
      newString = newString.replace(/...$/, '...');
    }
    return newString;
  };

  const resortCampaignData = async (sd) => {
    sortDescriptor.current = sd;
    page.current = 1;
    await setPages(1);
    await setCampaignData([]);
    await setHasMore(true);
    await fetchCampaignData();
  }

  const sortCampaignData = (data) => {
    // sortDescriptor looks like: {direction: 'descending', column: 'created_at'}
    const sd = sortDescriptor.current;
    return data.sort((a,b) => {
      if (sd['direction'] === 'descending') {
        if (b[sd['column']] > a[sd['column']]) {
          return 1;
        } else {
          return -1;
        }
      } else {
        if (b[sd['column']] < a[sd['column']]) {
          return 1;
        } else {
          return -1;
        }
      }
    });
  }

  const moveOrg = async (org) => {
    const campaign = campaignData?.current.filter(c => c._id === selectedKeys.currentKey)[0];
    const cid = campaign.id || campaign._id;
    let orgId = org || 'none';
    const result = await fetch(`/api/v1/campaign/${cid}?organization_id=${orgId || 'none'}`, {method: 'PUT'});
    if (result.status === 200) {
      alert(`Campaign '${campaign.subject}' moved successfully.`);
      window.location.reload();
    }
  };

  const setSelectedKeysWrapper = async (data) => {
    for (let i = 0; i < campaignData.current.length; i++) {
      const camp = campaignData.current[i];
      if (camp._id === data.currentKey) {
        // Copy to clipboard.
        // set copied flag..
        navigator.clipboard.writeText(genUrl(camp).props.href);
        // setCopied(camp);
      }
    }
    setSelectedKeys(data);
  }

  const showRecordsWrapper = (campaign) => {
    setShowRecordsModal(true);
    setRecordsCampaign(campaign);
  };

  const iCanEditSelected = () => {
    if (user?.admin) {
      return true;
    }
    const c = campaignData.current.filter(c => c._id === selectedKeys.currentKey)?.[0];
    if (c && c.user_id === user.id) {
      return true;
    }
    if (c && c.organization_id && c.organization_id === user.active_organization_id) {
      if (user?.active_organization?.member?.role?.indexOf('admin') !== -1) {
        return true;
      }
    }
    return false;
  }

  const orgForSelected = () => {
    if (selectedKeys && selectedKeys.currentKey && campaignData?.current) {
      const orgId = campaignData.current.filter(c => c._id === selectedKeys.currentKey)[0].organization_id;
      console.log(" ORG IS: " + orgId);
      return orgId;
    }
    return undefined;
  };

  return (
    <div style={{width: '98%', display: 'flex', flexDirection: 'column'}}>
      { showUser && <UserInfo myUser={user} userid={showUser} dismissModal={() => setShowUser(false)} /> }
      { showRecordsModal && recordsCampaign && <EmailRecordsModal dismissModal={() => setShowRecordsModal(false)}
                                                                  campaign={recordsCampaign} />
      }
      <Modal placement='top' isDismissable={true}
             isKeyboardDismissDisabled={true}
             onClose={() => setShowUpgradeModal(false)}
             isOpen={showUpgradeModal}>
        <ModalContent>
           <ModalHeader className='flex flex-col gap-1'>
             As an organizer, your voice matters. Now you can amplify it - add
             <b>{upgradeAmount === 500 ? "100 " : upgradeAmount === 4000 ? "1000 " : "unlimited "}</b>
             <i>individually customized, high-impact emails</i>
             to your campaign for only
             ${upgradeAmount / 100}. Each message is unique, helping you break through spam filters and
             command attention from key decision-makers. It's an affordable way to mobilize your cause
             and make supports' outreach count.
           </ModalHeader>
           <ModalBody>
             { upgradeCampaign &&
               <CheckoutFormEnvelope amount={upgradeAmount} userid={user.id} upgradeCampaign={upgradeCampaign} />
             }
           </ModalBody>
           <ModalFooter>
           </ModalFooter>
        </ModalContent>
      </Modal>
      { showCompletionModal && <CompletionModal campaign={campaignData.current.filter((d) => d._id === selectedKeys.currentKey)[0]}
                                                dismissModal={() => setShowCompletionModal(false)}
                               /> }
      { showResumeModal && <ResumeModal campaign={campaignData.current.filter((d) => d._id === selectedKeys.currentKey)[0]}
                                        dismissModal={() => setShowResumeModal(false)}
                               /> }
      { (selectedKeys && selectedKeys?.size > 0)
        && <div style={{display: 'flex', alignSelf: 'flex-end', marginBottom: '8px'}}>
             { iCanEditSelected() &&
                  <Button onPress={() => {
                      let deleteMe = campaignData.current.filter((d) => d._id === selectedKeys.currentKey)[0];
                      if (window.confirm('Delete: ' + ellipse(deleteMe.subject) + '?')) {
                        deleteCampaign(selectedKeys.currentKey);
                      }
                    }}
                          style={{fontSize: 'small'}}
                          color='danger' isIconOnly={window.innerWidth < 410}>{window.innerWidth < 410 ? 'Del' : 'Delete'}</Button>
             }
             <Button onPress={() => {
                       const c = campaignData.current.filter(c => c._id === selectedKeys.currentKey)[0];
                       if (c?._id || c?.id) {
                         delete(c.id);
                         delete(c._id);
                         delete(c.limit);
                         c.vanity_url = `new_${c.vanity_url}`;
                         editCampaign(c);
                       }
                     }}
                     style={{marginLeft: '10px', fontSize: 'small'}}
                     color='warning' isIconOnly={window.innerWidth < 410}>Copy</Button>

             { iCanEditSelected() && (user.active_organization_id || (user.organizations && user.organizations.length > 0))
                 && <Dropdown id={`pop_org_${selectedKeys.currentKey}`}>
                      <DropdownTrigger>
                        <Button style={{marginLeft: '10px', fontSize: 'small'}} isIconOnly={window.innerWidth < 410} color='warning'>
                          Move
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu>
                        <DropdownSection style={{maxHeight: '50vh', overflowY: 'scroll'}}>
                          <DropdownItem key='-' onPress={() => moveOrg()}> None ({user.my_campaign_count}) </DropdownItem>
                          { user.organizations.filter(o => o._id !== orgForSelected())
                            .map(org => <DropdownItem onPress={() => moveOrg(org._id)}
                                                                        key={org?._id}>{org?.name} ({org?.user_count || '0'})</DropdownItem>)
                          }
                        </DropdownSection>
                      </DropdownMenu>
                   </Dropdown>
             }

             { iCanEditSelected() &&
                 <Button onPress={() => editCampaign(campaignData.current.filter(c => c._id === selectedKeys.currentKey)[0])}
                         style={{marginLeft: '10px', fontSize: 'small'}}
                         isIconOnly={window.innerWidth < 410} color='warning'>Edit</Button>
             }
             { iCanEditSelected() ?
                 !campaignData.current.filter(c => c._id === selectedKeys.currentKey)[0]?.completion_text
                 ? <Button onPress={() => setShowCompletionModal(true)}
                           style={{padding: '0px', marginLeft: '10px', fontSize: 'small'}}
                           isIconOnly={window.innerWidth < 410} color='danger'>End</Button>
                 : <Button onPress={() => setShowResumeModal(true)}
                           style={{marginLeft: '10px', fontSize: 'small'}}
                           isIconOnly={window.innerWidth < 410} color='warning'>Resume Campaign</Button>
                 : ''
             }
             </div>
        }
      <div style={{padding: '10px', fontSize: '18px', display: 'flex', flexDirection: 'row-reverse', width: '100%'}}>
        <span style={{paddingLeft: '30px', textShadow: '1px 1px 2px #305054'}}>
           Total emails generated:
           <span style={{color: '#dfdfdf'}}> {recordCount && parseInt(recordCount).toLocaleString()}</span>
        </span>
        <span style={{textShadow: '1px 1px 2px #205054'}}>
           Total clicks generated:
           <span style={{color: '#dfdfdf'}}> {clickCount && parseInt(clickCount).toLocaleString()}</span>
        </span>
      </div>
      <Table selectionMode='multiple'
             shadow='sm'
             isHeaderSticky
             baseRef={scrollerRef}
             isStriped
             isLoading={fetching}
             loadingContent={<Spinner label='Loading...' />}
             selectedKeys={selectedKeys}
             classNames={{base: 'act-t-base', table: 'act-t-table'}}
             selectionBehavior="replace"
             color="success"
             onSelectionChange={setSelectedKeysWrapper}
             onSortChange={resortCampaignData}
             sortDescriptor={sortDescriptor.current}
             bottomContent={
               hasMore ? (
                 <div className="flex w-full justify-center">
                   <Spinner ref={loaderRef} color="white" />
                 </div>
               ) : null
             }
      >
        <TableHeader columns={9}>
          <TableColumn style={{backgroundColor: '#19b2c3', color: '#f0f0f0', minWidth: '200px'}} allowsSorting key='subject'>Your Campaign</TableColumn>
          <TableColumn style={{backgroundColor: '#19b2c3', color: '#f0f0f0', width: (user?.admin || user?.active_organization_id) ? '280px' : 0}}
                       allowsSorting={user?.admin ? true : false} key='user_id'>{(user?.admin || user?.active_organization_id) ? 'Owner' : ''}
          </TableColumn>
          <TableColumn style={{backgroundColor: '#19b2c3', color: '#f0f0f0', width: '70px', minWidth: '70px', maxWidth: '70px'}}
                       allowsSorting key='populated' align='center'>Status</TableColumn>
          <TableColumn style={{backgroundColor: '#19b2c3', color: '#f0f0f0', width: '80px', minWidth: '80px', maxWidth: '80px'}}
                       allowsSorting key='usage_counter' align='end'># of Clicks</TableColumn>
          <TableColumn style={{backgroundColor: '#19b2c3', color: '#f0f0f0', width: '80px', minWidth: '80px', maxWidth: '80px'}}
                       allowsSorting key='email_record_count' align='end'># of Sent</TableColumn>
          <TableColumn style={{backgroundColor: '#19b2c3', color: '#f0f0f0', width: '100px', minWidth: '100px', maxWidth: '100px'}}
                       allowsSorting key='limit' align='end'>Limit</TableColumn>
          <TableColumn style={{backgroundColor: '#19b2c3', color: '#f0f0f0', width: '120px', minWidth: '120px', maxWidth: '120px'}}
                       allowsSorting key='created_at'>Created</TableColumn>
          <TableColumn style={{backgroundColor: '#19b2c3', color: '#f0f0f0', width: '130px'}}
                       allowsSorting={user?.admin ? true : false} key='updated_at'>Updated</TableColumn>
          <TableColumn style={{backgroundColor: '#19b2c3', color: '#f0f0f0', width: '580px', minWidth: '580px', maxWidth: '580px'}}
                       allowsSorting key='vanity_url'>URL</TableColumn>
        </TableHeader>
        <TableBody isLoading={fetching} loadingContent={<Spinner color='white' />}>
          {
            campaignData.current
            ? sortCampaignData(campaignData.current).map((d, idx) => <TableRow key={d._id || d.id}>
              <TableCell style={{minWidth: '200px'}} className='htr'>
                <span style={{cursor: 'pointer'}}>
                  {user?.admin ? ellipse(d.subject, 40) : d.subject}
                </span>
              </TableCell>
              <TableCell style={{width: (user?.admin || user?.active_organization_id) ? '280px' : '0px'}}>
                {(user?.admin || user?.active_organization_id)
                 ? <span style={{textDecoration: 'underline', cursor: 'pointer', color: '#105065'}} onClick={() => setShowUser(d.user_id)}>{d.owner}</span>
                 : ''}
              </TableCell>
              <TableCell style={{width: '70px', minWidth: '70px', maxWidth: '70px'}} className='htr'>
                { d.completion_text ? "Completed" : d.populated ? <Checkbox defaultSelected color="success" /> : <Spinner size='sm' /> }
              </TableCell>
              <TableCell style={{width: '80px', minWidth: '80px', maxWidth: '80px'}} className='htr'>
                {parseInt(d.usage_counter).toLocaleString()}
              </TableCell>
              <TableCell style={{width: '80px', minWidth: '80px', maxWidth: '80px'}} className='htr'>
                <Button size='sm' onClick={() => showRecordsWrapper(d)} color='success' shape='circle'>
                  {parseInt(d.email_record_count).toLocaleString()}
                </Button>
              </TableCell>
              <TableCell style={{width: '100px', minWidth: '100px', maxWidth: '100px'}} className='htr'>
                {
                  d.limit > 999999
                  ? <b>Unlimited</b>
                  : (parseInt(d.limit).toLocaleString() || 10)
                }
                { " " }
                {(!d.limit || (d.limit < 999999)) && generatePopover(d)}
              </TableCell>
              <TableCell style={{width: '120px', minWidth: '120px', maxWidth: '120px'}} className='htr'>{new Date(d.created_at).toDateString().split(' ').slice(1,4).join(' ')}</TableCell>
              <TableCell style={{width: '130px'}} className='htr'><TimeAgo date={new Date(d.updated_at)} /></TableCell>
              <TableCell style={{width: '580px', minWidth: '580px', maxWidth: '580px'}} className='htr'>
                {genUrl(d)}
                {' '}
                <img alt='copy' style={{width: '20px', display: 'inline'}}
                     onClick={() => setCopied(d)} src={clipboardImage}
                />
                <span style={{color: '#4080f0', fontWeight: 'bold'}} id={`copied_${d._id}`}> </span>
              </TableCell>
            </TableRow>
            )
            : 'No campaigns'
          }
        </TableBody>
      </Table>
    </div>
  );
}

export default CampaignTable;
